import React, { useState, useEffect } from "react";
import styles from "./Faq.module.css";
import { getFaqData, FAQData } from "../../lib/getFaq";

const Faq: React.FC = () => {
  const [activeQuestion, setActiveQuestion] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [faqData, setFaqData] = useState<FAQData>({ categories: [] });
  const [filteredFaqs, setFilteredFaqs] = useState<FAQData["categories"]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch FAQ data on component mount
  useEffect(() => {
    const loadFaqData = async () => {
      try {
        setLoading(true);
        const data = await getFaqData();
        setFaqData(data);
        setFilteredFaqs(data.categories);
        setLoading(false);
      } catch (err) {
        setError("Failed to load FAQ data. Please try again later.");
        setLoading(false);
        console.error("Error loading FAQ data:", err);
      }
    };

    loadFaqData();
  }, []);

  // Handle search functionality
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredFaqs(faqData.categories);
      return;
    }

    const lowercasedSearch = searchTerm.toLowerCase();

    const filtered = faqData.categories
      .map((category) => {
        const matchedFaqs = category.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(lowercasedSearch) ||
            faq.answer.toLowerCase().includes(lowercasedSearch)
        );

        if (matchedFaqs.length > 0) {
          return {
            ...category,
            faqs: matchedFaqs,
          };
        }
        return null;
      })
      .filter(Boolean) as typeof faqData.categories;

    setFilteredFaqs(filtered);
  }, [searchTerm, faqData.categories]);

  const toggleQuestion = (id: number) => {
    setActiveQuestion(activeQuestion === id ? null : id);
  };

  if (loading) {
    return (
      <div className="container">
        <div className={styles.faqContainer}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Cargando preguntas frecuentes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className={styles.faqContainer}>
          <div className={styles.errorContainer}>
            <p className={styles.error}>{error}</p>
            <button
              className="buttonSecondary"
              onClick={() => window.location.reload()}
            >
              Intentar nuevamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className={styles.faqContainer}>
        <div className={styles.faqHeader}>
          <h1 className={styles.faqTitle + " primary"}>Preguntas Frecuentes</h1>
          <p className={styles.faqSubtitle}>
            Encuentra respuestas a las preguntas más comunes sobre nuestros
            productos y servicios.
          </p>

          <div className={styles.faqSearch}>
            <input
              type="text"
              placeholder="Buscar preguntas..."
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className={styles.searchIcon}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
                  stroke="var(--gray-300)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 21L16.65 16.65"
                  stroke="var(--gray-300)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {filteredFaqs.length > 0 ? (
          filteredFaqs.map((category) => (
            <div key={category.id} className={styles.faqSection}>
              <h2 className={styles.sectionTitle}>{category.name}</h2>

              {category.faqs.map((faq) => (
                <div key={faq.id} className={styles.faqItem}>
                  <div
                    className={`${styles.faqQuestion} ${
                      activeQuestion === faq.id ? styles.active : ""
                    }`}
                    onClick={() => toggleQuestion(faq.id)}
                  >
                    <span>{faq.question}</span>
                    <div
                      className={`${styles.faqArrow} ${
                        activeQuestion === faq.id ? styles.open : ""
                      }`}
                    >
                      <svg
                        width="14"
                        height="8"
                        viewBox="0 0 14 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1L7 7L13 1"
                          stroke="var(--blue-evefant-dark-3)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <div
                    className={`${styles.faqAnswer} ${
                      activeQuestion === faq.id ? styles.open : ""
                    }`}
                  >
                    <div className={styles.answerContent}>{faq.answer}</div>
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className={styles.noResults}>
            <p>
              No se encontraron resultados para tu búsqueda. Intenta con otros
              términos.
            </p>
          </div>
        )}

        <div
          style={{
            marginTop: 40,
            padding: 32,
            background: "var(--info-color-background)",
            borderRadius: 12,
            textAlign: "center",
            color: "var(--info-color-text)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          }}
        >
          <h3 style={{ marginBottom: 12, color: "var(--primary-color)" }}>
            ¿No encontraste la respuesta que buscabas?
          </h3>
          <p style={{ marginBottom: 24 }}>
            Si tienes alguna otra pregunta, puedes contactarnos directamente por
            WhatsApp y te ayudaremos lo antes posible.
          </p>
          <a
            href="https://wa.me/51999999999"
            target="_blank"
            rel="noopener noreferrer"
            className="buttonMain"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              textDecoration: "none",
              fontSize: 18,
              width: "auto",
              padding: "12px 32px",
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
              style={{ marginRight: 8 }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.52 3.48A12 12 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.12.56 4.18 1.62 6.01L0 24l6.18-1.62A11.97 11.97 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52ZM12 22c-1.77 0-3.5-.46-5.01-1.33l-.36-.21-3.67.96.98-3.57-.23-.37A9.97 9.97 0 1 1 22 12c0 5.52-4.48 10-10 10Zm5.2-7.8c-.29-.15-1.7-.84-1.96-.94-.26-.1-.45-.15-.64.15-.19.29-.74.94-.91 1.13-.17.19-.34.21-.63.07-.29-.15-1.23-.45-2.34-1.44-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.44.13-.58.13-.13.29-.34.43-.51.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.15-.64-1.54-.88-2.11-.23-.56-.47-.48-.64-.49-.16-.01-.36-.01-.56-.01-.19 0-.5.07-.76.36-.26.29-1 1-.99 2.43.01 1.43 1.03 2.81 1.18 3 .15.19 2.03 3.11 4.93 4.24.69.3 1.23.48 1.65.62.69.22 1.31.19 1.8.12.55-.08 1.7-.7 1.94-1.37.24-.67.24-1.25.17-1.37-.07-.12-.26-.19-.55-.34Z"
                fill="#fff"
              />
            </svg>
            Escríbenos por WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default Faq;
